import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataStore, useUIStore } from '../stores';
import dataService from '../services/DataService';
import './RoomDetail.css';

function RoomDetail() {
  const { projectId, roomId } = useParams();
  const navigate = useNavigate();
  const showSuccessNotification = useUIStore(state => state.showSuccessNotification);
  const showErrorNotification = useUIStore(state => state.showErrorNotification);

  const [room, setRoom] = useState(null);
  const [roomUpdates, setRoomUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline' or 'updates'
  const [showImageForm, setShowImageForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [imageFormData, setImageFormData] = useState({
    file: null,
    description: ''
  });
  const [updateFormData, setUpdateFormData] = useState({
    remarks: '',
    files: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadRoomData();
  }, [projectId, roomId]);

  const loadRoomData = async () => {
    setIsLoading(true);
    try {
      // Load room details with timeline
      const roomData = await dataService.getRoomTimeline(parseInt(projectId), parseInt(roomId));
      setRoom(roomData);
      
      // If room has updates, set them (they might be in the response or separate)
      // For now, updates might be empty array - will be populated when updates are created
      if (roomData.updates) {
        setRoomUpdates(roomData.updates);
      } else {
        setRoomUpdates([]);
      }
    } catch (error) {
      console.error('Error loading room:', error);
      showErrorNotification('Failed to load room details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    setImageFormData(prev => ({
      ...prev,
      file: file
    }));
  };

  const handleUpdateFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUpdateFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeUpdateFile = (index) => {
    setUpdateFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const validateImageForm = () => {
    const newErrors = {};
    
    if (!imageFormData.file) {
      newErrors.file = 'Please select an image file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateImageForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dataService.addRoomTimelineImage(
        parseInt(projectId),
        parseInt(roomId),
        imageFormData.file,
        imageFormData.description.trim()
      );
      
      showSuccessNotification('Image added to timeline successfully');
      setImageFormData({ file: null, description: '' });
      setShowImageForm(false);
      await loadRoomData();
    } catch (error) {
      console.error('Error adding timeline image:', error);
      let errorMessage = 'Failed to add image';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const createdUpdate = await dataService.createRoomUpdate(
        parseInt(projectId),
        parseInt(roomId),
        updateFormData.remarks.trim(),
        updateFormData.files
      );
      
      showSuccessNotification('Room update created successfully');
      setUpdateFormData({ remarks: '', files: [] });
      setShowUpdateForm(false);
      
      // Add the new update to the list and reload room data
      if (createdUpdate) {
        setRoomUpdates(prev => [createdUpdate, ...prev]);
      }
      await loadRoomData();
    } catch (error) {
      console.error('Error creating room update:', error);
      let errorMessage = 'Failed to create update';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e, formType) => {
    const { name, value } = e.target;
    
    if (formType === 'image') {
      setImageFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (formType === 'update') {
      setUpdateFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getImageUrl = (image) => {
    if (typeof image === 'string') return image;
    return image.file_path || image.url || image;
  };

  if (isLoading) {
    return (
      <div className="room-detail-container">
        <div className="room-detail-loading">
          <p>Loading room details...</p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="room-detail-container">
        <div className="room-detail-error">
          <p>Room not found</p>
          <button onClick={() => navigate(`/admin/projects/${projectId}`)} className="btn-primary">
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="room-detail-container">
      <div className="room-detail-header">
        <button onClick={() => navigate(`/admin/projects/${projectId}`)} className="back-button">
          ← Back to Project
        </button>
        <div className="room-detail-title">
          <h1>{room.room_name}</h1>
          {room.description && <p className="room-description-header">{room.description}</p>}
        </div>
      </div>

      <div className="room-detail-content">
        <div className="room-tabs">
          <button
            className={activeTab === 'timeline' ? 'active' : ''}
            onClick={() => setActiveTab('timeline')}
          >
            📸 Timeline Images
          </button>
          <button
            className={activeTab === 'updates' ? 'active' : ''}
            onClick={() => setActiveTab('updates')}
          >
            📝 Updates
          </button>
        </div>

        {activeTab === 'timeline' && (
          <div className="room-tab-content">
            <div className="section-header">
              <h2>Timeline Images</h2>
              <button 
                onClick={() => setShowImageForm(!showImageForm)}
                className="btn-primary"
              >
                {showImageForm ? 'Cancel' : '+ Add Image'}
              </button>
            </div>

            {showImageForm && (
              <form onSubmit={handleImageSubmit} className="image-upload-form">
                <div className="form-group">
                  <label htmlFor="image_file">Select Image *</label>
                  <input
                    type="file"
                    id="image_file"
                    name="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    disabled={isSubmitting}
                  />
                  {errors.file && <span className="error-text">{errors.file}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="image_description">Description (Optional)</label>
                  <input
                    type="text"
                    id="image_description"
                    name="description"
                    value={imageFormData.description}
                    onChange={(e) => handleChange(e, 'image')}
                    placeholder="Add a description for this image..."
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={isSubmitting || !imageFormData.file}
                  >
                    {isSubmitting ? 'Uploading...' : 'Upload Image'}
                  </button>
                </div>
              </form>
            )}

            {!room.timeline_images || room.timeline_images.length === 0 ? (
              <div className="no-content">
                <p>No timeline images yet. Upload the first image to get started!</p>
              </div>
            ) : (
              <div className="timeline-images-grid">
                {room.timeline_images.map((image, index) => (
                  <div key={image.id || index} className="timeline-image-card">
                    <img 
                      src={getImageUrl(image)} 
                      alt={image.description || `Timeline image ${index + 1}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <div className="image-error-placeholder" style={{ display: 'none' }}>
                      Image not available
                    </div>
                    {image.description && (
                      <p className="image-description">{image.description}</p>
                    )}
                    <span className="image-date">{formatDate(image.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'updates' && (
          <div className="room-tab-content">
            <div className="section-header">
              <h2>Room Updates</h2>
              <button 
                onClick={() => setShowUpdateForm(!showUpdateForm)}
                className="btn-primary"
              >
                {showUpdateForm ? 'Cancel' : '+ Add Update'}
              </button>
            </div>

            {showUpdateForm && (
              <form onSubmit={handleUpdateSubmit} className="update-form">
                <div className="form-group">
                  <label htmlFor="update_remarks">Remarks (Optional)</label>
                  <textarea
                    id="update_remarks"
                    name="remarks"
                    value={updateFormData.remarks}
                    onChange={(e) => handleChange(e, 'update')}
                    rows="4"
                    placeholder="Add remarks or description for this update..."
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="update_files">Upload Images *</label>
                  <input
                    type="file"
                    id="update_files"
                    name="files"
                    onChange={handleUpdateFileChange}
                    multiple
                    accept="image/*"
                    disabled={isSubmitting}
                  />
                  {updateFormData.files.length > 0 && (
                    <div className="file-list">
                      {updateFormData.files.map((file, index) => (
                        <div key={index} className="file-item">
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeUpdateFile(index)}
                            className="remove-file-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <small className="form-hint">Select one or more images for this update.</small>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={isSubmitting || updateFormData.files.length === 0}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Update'}
                  </button>
                </div>
              </form>
            )}

            {(!roomUpdates || roomUpdates.length === 0) ? (
              <div className="no-content">
                <p>No updates yet. Create the first update to get started!</p>
              </div>
            ) : (
              <div className="updates-list">
                {roomUpdates.map((update) => (
                  <div key={update.id} className="update-item">
                    <div className="update-header">
                      <div>
                        <span className="update-author">{update.created_by_name || 'Admin'}</span>
                        <span className="update-date">{formatDate(update.created_at)}</span>
                      </div>
                    </div>
                    {update.remarks && (
                      <div className="update-content">
                        <p>{update.remarks}</p>
                      </div>
                    )}
                    {update.media && update.media.length > 0 && (
                      <div className="update-media">
                        {update.media.map((media, index) => (
                          <div key={media.id || index} className="media-item">
                            <img 
                              src={getImageUrl(media)} 
                              alt={`Update image ${index + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RoomDetail;

